import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MY_RESUME, SAVE_RESUME, ME } from '../graphql';
import { Save, Printer, Plus, Trash2, Eye, Edit3, Download } from 'lucide-react';

// ─── DEFAULT RESUME STRUCTURE ────────────────────────────────────────────────
const DEFAULT_DATA = {
  personalInfo: { name: '', email: '', phone: '', location: '', github: '', linkedin: '', website: '' },
  summary: '',
  education: [{ id: '1', institution: '', degree: '', field: '', year: '', cgpa: '', achievements: '' }],
  experience: [{ id: '1', company: '', role: '', duration: '', location: '', points: [''] }],
  projects: [{ id: '1', name: '', description: '', techStack: '', link: '', points: [''] }],
  skills: { languages: '', frameworks: '', tools: '', databases: '', softSkills: '' },
  certifications: [{ id: '1', name: '', issuer: '', date: '', link: '' }],
  achievements: [''],
};

const uid = () => Math.random().toString(36).slice(2, 8);

// ─── RESUME TEMPLATES ────────────────────────────────────────────────────────

function ModernTemplate({ data }) {
  const p = data.personalInfo || {};
  const skills = data.skills || {};
  const allSkills = [skills.languages, skills.frameworks, skills.tools, skills.databases, skills.softSkills].filter(Boolean).join(' · ');

  return (
    <div className="resume-page font-sans text-gray-900 text-sm leading-relaxed bg-white" style={{ fontFamily: 'Georgia, serif', fontSize: '11px', lineHeight: '1.5' }}>
      {/* Header */}
      <div className="text-center border-b-2 border-gray-800 pb-3 mb-4">
        <h1 style={{ fontSize: '22px', fontWeight: 'bold', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '4px' }}>{p.name || 'Your Name'}</h1>
        <div style={{ fontSize: '10px', color: '#555', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '8px' }}>
          {p.email && <span>{p.email}</span>}
          {p.phone && <span>| {p.phone}</span>}
          {p.location && <span>| {p.location}</span>}
          {p.github && <span>| github.com/{p.github.replace(/.*github\.com\//,'')}</span>}
          {p.linkedin && <span>| linkedin.com/in/{p.linkedin.replace(/.*linkedin\.com\/in\//,'')}</span>}
          {p.website && <span>| {p.website}</span>}
        </div>
      </div>

      {/* Summary */}
      {data.summary && (
        <Section title="Professional Summary">
          <p style={{ textAlign: 'justify' }}>{data.summary}</p>
        </Section>
      )}

      {/* Education */}
      {data.education?.some(e => e.institution) && (
        <Section title="Education">
          {data.education.filter(e=>e.institution).map(e => (
            <div key={e.id} style={{ marginBottom: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>{e.institution}</strong>
                <span style={{ color: '#555' }}>{e.year}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontStyle: 'italic' }}>{e.degree}{e.field ? ` — ${e.field}` : ''}</span>
                {e.cgpa && <span>CGPA: {e.cgpa}</span>}
              </div>
              {e.achievements && <p style={{ color: '#444', marginTop: '2px' }}>{e.achievements}</p>}
            </div>
          ))}
        </Section>
      )}

      {/* Skills */}
      {allSkills && (
        <Section title="Technical Skills">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px' }}>
            {[['Languages & CS', skills.languages],['Frameworks & Libraries',skills.frameworks],['Tools & Platforms',skills.tools],['Databases',skills.databases],['Soft Skills',skills.softSkills]].filter(([,v])=>v).map(([k,v])=>(
              <p key={k}><strong>{k}:</strong> {v}</p>
            ))}
          </div>
        </Section>
      )}

      {/* Experience */}
      {data.experience?.some(e => e.company) && (
        <Section title="Work Experience">
          {data.experience.filter(e=>e.company).map(e => (
            <div key={e.id} style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>{e.role}</strong>
                <span style={{ color: '#555' }}>{e.duration}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontStyle: 'italic', color: '#444' }}>
                <span>{e.company}</span>{e.location && <span>{e.location}</span>}
              </div>
              <ul style={{ paddingLeft: '14px', marginTop: '3px' }}>
                {e.points.filter(Boolean).map((pt, i) => <li key={i} style={{ listStyleType: 'disc' }}>{pt}</li>)}
              </ul>
            </div>
          ))}
        </Section>
      )}

      {/* Projects */}
      {data.projects?.some(p => p.name) && (
        <Section title="Projects">
          {data.projects.filter(p=>p.name).map(p => (
            <div key={p.id} style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>{p.name}</strong>
                {p.link && <a href={p.link} style={{ color: '#3b82f6', fontSize: '10px' }}>Link</a>}
              </div>
              {p.techStack && <p style={{ color: '#555', fontStyle: 'italic' }}>Tech: {p.techStack}</p>}
              {p.description && <p style={{ marginTop: '2px' }}>{p.description}</p>}
              <ul style={{ paddingLeft: '14px', marginTop: '2px' }}>
                {p.points.filter(Boolean).map((pt, i) => <li key={i} style={{ listStyleType: 'disc' }}>{pt}</li>)}
              </ul>
            </div>
          ))}
        </Section>
      )}

      {/* Certifications */}
      {data.certifications?.some(c => c.name) && (
        <Section title="Certifications">
          {data.certifications.filter(c=>c.name).map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span><strong>{c.name}</strong>{c.issuer ? ` — ${c.issuer}` : ''}</span>
              <span style={{ color: '#555' }}>{c.date}</span>
            </div>
          ))}
        </Section>
      )}

      {/* Achievements */}
      {data.achievements?.some(Boolean) && (
        <Section title="Achievements & Awards">
          <ul style={{ paddingLeft: '14px' }}>
            {data.achievements.filter(Boolean).map((a, i) => <li key={i} style={{ listStyleType: 'disc' }}>{a}</li>)}
          </ul>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '10px' }}>
      <h2 style={{ fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid #333', paddingBottom: '2px', marginBottom: '5px' }}>{title}</h2>
      {children}
    </div>
  );
}

// ─── EDITOR SECTION COMPONENTS ───────────────────────────────────────────────
function Field({ label, value, onChange, placeholder, type = 'text', multiline }) {
  const cls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-400 transition-colors";
  return (
    <div>
      {label && <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>}
      {multiline
        ? <textarea className={`${cls} resize-none h-20`} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} />
        : <input className={cls} type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function ResumePage() {
  const { data: resumeData } = useQuery(MY_RESUME);
  const { data: meData } = useQuery(ME);
  const [saveResume, { loading: saving }] = useMutation(SAVE_RESUME, { refetchQueries: [MY_RESUME] });

  const [template, setTemplate] = useState('modern');
  const [resumeJson, setResumeJson] = useState(DEFAULT_DATA);
  const [view, setView] = useState('edit'); // 'edit' | 'preview'
  const [activeSection, setActiveSection] = useState('personal');
  const [saved, setSaved] = useState(false);
  const printRef = useRef();

  // Load from DB or prefill from user profile
  useEffect(() => {
    if (resumeData?.myResume?.data) {
      try { setResumeJson(JSON.parse(resumeData.myResume.data)); setTemplate(resumeData.myResume.template); } catch(_) {}
    } else if (meData?.me) {
      const u = meData.me;
      setResumeJson(d => ({ ...d, personalInfo: { ...d.personalInfo, name: u.name||'', email: u.email||'' }}));
    }
  }, [resumeData, meData]);

  const update = (path, value) => {
    setResumeJson(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
    setSaved(false);
  };

  const handleSave = async () => {
    await saveResume({ variables: { template, data: JSON.stringify(resumeJson) } });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Resume - ${resumeJson.personalInfo.name || 'Resume'}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Georgia, serif; font-size: 11px; line-height: 1.5; color: #111; }
        @page { size: A4; margin: 18mm 16mm; }
        @media print { body { -webkit-print-color-adjust: exact; } }
      </style></head><body>${printContents}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  const r = resumeJson;

  const SECTIONS = [
    { id: 'personal', label: 'Personal' },
    { id: 'summary',  label: 'Summary' },
    { id: 'education',label: 'Education' },
    { id: 'skills',   label: 'Skills' },
    { id: 'experience',label:'Experience' },
    { id: 'projects', label: 'Projects' },
    { id: 'certs',    label: 'Certifications' },
    { id: 'achieve',  label: 'Achievements' },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resume Builder</h1>
          <p className="text-gray-500 text-sm mt-0.5">Build, preview, and print your ATS-friendly resume</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            <button onClick={() => setView('edit')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${view==='edit'?'bg-white shadow text-gray-900':'text-gray-500'}`}><Edit3 size={14}/> Edit</button>
            <button onClick={() => setView('preview')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${view==='preview'?'bg-white shadow text-gray-900':'text-gray-500'}`}><Eye size={14}/> Preview</button>
          </div>
          <button onClick={handleSave} disabled={saving} className="btn-secondary flex items-center gap-1.5 text-sm">
            <Save size={14}/>{saving ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
          </button>
          <button onClick={handlePrint} className="btn-primary flex items-center gap-1.5 text-sm">
            <Download size={14}/> Export PDF
          </button>
        </div>
      </div>

      {/* Template picker */}
      <div className="flex items-center gap-3 mb-5 p-3 bg-gray-50 rounded-xl">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Template:</span>
        {['modern','classic'].map(t => (
          <button key={t} onClick={() => setTemplate(t)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${template===t?'bg-brand-600 text-white':'bg-white border border-gray-200 text-gray-600 hover:border-brand-300'}`}>
            {t}
          </button>
        ))}
        <span className="text-xs text-gray-400 ml-auto">ATS-optimised single-column layout · Exports as A4 PDF</span>
      </div>

      <div className={`flex gap-5 ${view === 'preview' ? 'flex-col' : ''}`}>
        {/* ── EDITOR PANEL ── */}
        {view === 'edit' && (
          <div className="w-80 flex-shrink-0">
            {/* Section tabs (vertical) */}
            <div className="flex flex-col gap-0.5 mb-4">
              {SECTIONS.map(s => (
                <button key={s.id} onClick={() => setActiveSection(s.id)}
                  className={`text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeSection===s.id?'bg-brand-50 text-brand-700':'text-gray-600 hover:bg-gray-50'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── FORM CONTENT ── */}
        {view === 'edit' && (
          <div className="flex-1 card !p-5 space-y-4 overflow-y-auto max-h-[75vh]">

            {activeSection === 'personal' && (
              <>
                <h3 className="font-semibold text-gray-800">Personal Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Full Name" value={r.personalInfo.name} onChange={v=>update('personalInfo.name',v)} placeholder="Avi Kumar" />
                  <Field label="Email" value={r.personalInfo.email} onChange={v=>update('personalInfo.email',v)} placeholder="avi@email.com" />
                  <Field label="Phone" value={r.personalInfo.phone} onChange={v=>update('personalInfo.phone',v)} placeholder="+91 9876543210" />
                  <Field label="Location" value={r.personalInfo.location} onChange={v=>update('personalInfo.location',v)} placeholder="Bengaluru, KA" />
                  <Field label="GitHub URL / username" value={r.personalInfo.github} onChange={v=>update('personalInfo.github',v)} placeholder="github.com/avikumar" />
                  <Field label="LinkedIn URL" value={r.personalInfo.linkedin} onChange={v=>update('personalInfo.linkedin',v)} placeholder="linkedin.com/in/avikumar" />
                  <Field label="Portfolio / Website" value={r.personalInfo.website} onChange={v=>update('personalInfo.website',v)} placeholder="avikumar.dev" />
                </div>
              </>
            )}

            {activeSection === 'summary' && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">Professional Summary</h3>
                  <span className="text-xs text-gray-400">2–4 lines that recruiters read first</span>
                </div>
                <Field multiline value={r.summary} onChange={v=>update('summary',v)} placeholder="Final-year CSE student at XYZ University with strong DSA fundamentals and 2 internship experiences in full-stack development. Passionate about building scalable systems. Seeking a challenging SDE role to contribute and grow." />
                <div className="bg-blue-50 rounded-xl p-3">
                  <p className="text-xs text-blue-700 font-medium mb-1">Tip: Best summary formula</p>
                  <p className="text-xs text-blue-600">[Year] [Degree] student at [College] with [X] years experience in [skill]. [1 achievement]. Seeking [role] to [goal].</p>
                </div>
              </>
            )}

            {activeSection === 'education' && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">Education</h3>
                  <button onClick={() => setResumeJson(d=>({...d,education:[...d.education,{id:uid(),institution:'',degree:'',field:'',year:'',cgpa:'',achievements:''}]}))} className="text-xs text-brand-600 hover:underline flex items-center gap-1"><Plus size={12}/> Add</button>
                </div>
                {r.education.map((edu, i) => (
                  <div key={edu.id} className="border border-gray-100 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-gray-500">#{i+1}</span>
                      {r.education.length > 1 && <button onClick={() => setResumeJson(d=>({...d,education:d.education.filter(e=>e.id!==edu.id)}))} className="text-gray-300 hover:text-red-400"><Trash2 size={13}/></button>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Institution" value={edu.institution} onChange={v=>{const e=[...r.education];e[i]={...e[i],institution:v};update('education',e);}} placeholder="IIT Bombay" />
                      <Field label="Degree" value={edu.degree} onChange={v=>{const e=[...r.education];e[i]={...e[i],degree:v};update('education',e);}} placeholder="B.Tech" />
                      <Field label="Field / Branch" value={edu.field} onChange={v=>{const e=[...r.education];e[i]={...e[i],field:v};update('education',e);}} placeholder="Computer Science" />
                      <Field label="Year (e.g. 2024)" value={edu.year} onChange={v=>{const e=[...r.education];e[i]={...e[i],year:v};update('education',e);}} placeholder="2021–2025" />
                      <Field label="CGPA / %" value={edu.cgpa} onChange={v=>{const e=[...r.education];e[i]={...e[i],cgpa:v};update('education',e);}} placeholder="8.4/10" />
                      <Field label="Achievements (optional)" value={edu.achievements} onChange={v=>{const e=[...r.education];e[i]={...e[i],achievements:v};update('education',e);}} placeholder="Department Topper, 2022" />
                    </div>
                  </div>
                ))}
              </>
            )}

            {activeSection === 'skills' && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">Technical Skills</h3>
                  <span className="text-xs text-gray-400">Comma-separated</span>
                </div>
                <div className="space-y-3">
                  <Field label="Languages" value={r.skills.languages} onChange={v=>update('skills.languages',v)} placeholder="Python, Java, C++, JavaScript, SQL" />
                  <Field label="Frameworks & Libraries" value={r.skills.frameworks} onChange={v=>update('skills.frameworks',v)} placeholder="React, Node.js, Spring Boot, FastAPI" />
                  <Field label="Tools & Platforms" value={r.skills.tools} onChange={v=>update('skills.tools',v)} placeholder="Git, Docker, AWS, Linux, VS Code" />
                  <Field label="Databases" value={r.skills.databases} onChange={v=>update('skills.databases',v)} placeholder="MySQL, MongoDB, PostgreSQL, Redis" />
                  <Field label="Soft Skills" value={r.skills.softSkills} onChange={v=>update('skills.softSkills',v)} placeholder="Problem Solving, Team Leadership, Communication" />
                </div>
              </>
            )}

            {activeSection === 'experience' && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">Work Experience</h3>
                  <button onClick={() => setResumeJson(d=>({...d,experience:[...d.experience,{id:uid(),company:'',role:'',duration:'',location:'',points:['']}]}))} className="text-xs text-brand-600 hover:underline flex items-center gap-1"><Plus size={12}/> Add</button>
                </div>
                {r.experience.map((exp, i) => (
                  <div key={exp.id} className="border border-gray-100 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-xs font-bold text-gray-500">Experience #{i+1}</span>
                      {r.experience.length > 1 && <button onClick={() => setResumeJson(d=>({...d,experience:d.experience.filter(e=>e.id!==exp.id)}))} className="text-gray-300 hover:text-red-400"><Trash2 size={13}/></button>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Company" value={exp.company} onChange={v=>{const e=[...r.experience];e[i]={...e[i],company:v};update('experience',e);}} placeholder="Google" />
                      <Field label="Role / Title" value={exp.role} onChange={v=>{const e=[...r.experience];e[i]={...e[i],role:v};update('experience',e);}} placeholder="Software Engineering Intern" />
                      <Field label="Duration" value={exp.duration} onChange={v=>{const e=[...r.experience];e[i]={...e[i],duration:v};update('experience',e);}} placeholder="May 2024 – Jul 2024" />
                      <Field label="Location" value={exp.location} onChange={v=>{const e=[...r.experience];e[i]={...e[i],location:v};update('experience',e);}} placeholder="Bengaluru / Remote" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Bullet Points (use STAR: Situation→Task→Action→Result)</label>
                      {exp.points.map((pt, pi) => (
                        <div key={pi} className="flex gap-2 mb-1.5">
                          <input className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand-400" value={pt} onChange={e=>{const arr=[...exp.points];arr[pi]=e.target.value;const ex=[...r.experience];ex[i]={...ex[i],points:arr};update('experience',ex);}} placeholder="Built a microservice that reduced API latency by 40% serving 1M+ requests/day" />
                          {exp.points.length > 1 && <button onClick={()=>{const arr=exp.points.filter((_,j)=>j!==pi);const ex=[...r.experience];ex[i]={...ex[i],points:arr};update('experience',ex);}} className="text-gray-300 hover:text-red-400"><Trash2 size={13}/></button>}
                        </div>
                      ))}
                      <button onClick={()=>{const ex=[...r.experience];ex[i]={...ex[i],points:[...ex[i].points,'']};update('experience',ex);}} className="text-xs text-brand-500 hover:underline flex items-center gap-1 mt-1"><Plus size={11}/> Add bullet</button>
                    </div>
                  </div>
                ))}
                <div className="bg-amber-50 rounded-xl p-3">
                  <p className="text-xs text-amber-700 font-medium mb-1">STAR bullet tip</p>
                  <p className="text-xs text-amber-600">"<strong>Developed</strong> [what] using [tech] that [impact/metric]" — e.g. "Developed REST API using FastAPI that reduced checkout time by 35%"</p>
                </div>
              </>
            )}

            {activeSection === 'projects' && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">Projects</h3>
                  <button onClick={() => setResumeJson(d=>({...d,projects:[...d.projects,{id:uid(),name:'',description:'',techStack:'',link:'',points:['']}]}))} className="text-xs text-brand-600 hover:underline flex items-center gap-1"><Plus size={12}/> Add</button>
                </div>
                {r.projects.map((proj, i) => (
                  <div key={proj.id} className="border border-gray-100 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-xs font-bold text-gray-500">Project #{i+1}</span>
                      {r.projects.length > 1 && <button onClick={() => setResumeJson(d=>({...d,projects:d.projects.filter(p=>p.id!==proj.id)}))} className="text-gray-300 hover:text-red-400"><Trash2 size={13}/></button>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Project Name" value={proj.name} onChange={v=>{const p=[...r.projects];p[i]={...p[i],name:v};update('projects',p);}} placeholder="aura" />
                      <Field label="Tech Stack" value={proj.techStack} onChange={v=>{const p=[...r.projects];p[i]={...p[i],techStack:v};update('projects',p);}} placeholder="React, Node.js, GraphQL, SQLite" />
                      <Field label="GitHub/Live Link" value={proj.link} onChange={v=>{const p=[...r.projects];p[i]={...p[i],link:v};update('projects',p);}} placeholder="https://github.com/..." />
                      <Field label="One-line Description" value={proj.description} onChange={v=>{const p=[...r.projects];p[i]={...p[i],description:v};update('projects',p);}} placeholder="College super-app for 500+ students" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Bullet Points</label>
                      {proj.points.map((pt, pi) => (
                        <div key={pi} className="flex gap-2 mb-1.5">
                          <input className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand-400" value={pt} onChange={e=>{const arr=[...proj.points];arr[pi]=e.target.value;const ps=[...r.projects];ps[i]={...ps[i],points:arr};update('projects',ps);}} placeholder="Implemented real-time notifications using WebSockets for 1000+ concurrent users" />
                          {proj.points.length > 1 && <button onClick={()=>{const arr=proj.points.filter((_,j)=>j!==pi);const ps=[...r.projects];ps[i]={...ps[i],points:arr};update('projects',ps);}} className="text-gray-300 hover:text-red-400"><Trash2 size={13}/></button>}
                        </div>
                      ))}
                      <button onClick={()=>{const ps=[...r.projects];ps[i]={...ps[i],points:[...ps[i].points,'']};update('projects',ps);}} className="text-xs text-brand-500 hover:underline flex items-center gap-1 mt-1"><Plus size={11}/> Add bullet</button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {activeSection === 'certs' && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">Certifications</h3>
                  <button onClick={() => setResumeJson(d=>({...d,certifications:[...d.certifications,{id:uid(),name:'',issuer:'',date:'',link:''}]}))} className="text-xs text-brand-600 hover:underline flex items-center gap-1"><Plus size={12}/> Add</button>
                </div>
                {r.certifications.map((cert, i) => (
                  <div key={cert.id} className="border border-gray-100 rounded-xl p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Certificate Name" value={cert.name} onChange={v=>{const c=[...r.certifications];c[i]={...c[i],name:v};update('certifications',c);}} placeholder="AWS Cloud Practitioner" />
                      <Field label="Issuer" value={cert.issuer} onChange={v=>{const c=[...r.certifications];c[i]={...c[i],issuer:v};update('certifications',c);}} placeholder="Amazon Web Services" />
                      <Field label="Date" value={cert.date} onChange={v=>{const c=[...r.certifications];c[i]={...c[i],date:v};update('certifications',c);}} placeholder="Jun 2024" />
                      <Field label="Credential Link" value={cert.link} onChange={v=>{const c=[...r.certifications];c[i]={...c[i],link:v};update('certifications',c);}} placeholder="https://..." />
                    </div>
                    {r.certifications.length > 1 && <button onClick={() => setResumeJson(d=>({...d,certifications:d.certifications.filter(c=>c.id!==cert.id)}))} className="text-xs text-red-400 hover:underline flex items-center gap-1"><Trash2 size={11}/> Remove</button>}
                  </div>
                ))}
              </>
            )}

            {activeSection === 'achieve' && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">Achievements & Awards</h3>
                  <button onClick={() => setResumeJson(d=>({...d,achievements:[...d.achievements,'']}))} className="text-xs text-brand-600 hover:underline flex items-center gap-1"><Plus size={12}/> Add</button>
                </div>
                {r.achievements.map((ach, i) => (
                  <div key={i} className="flex gap-2">
                    <input className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-400" value={ach} onChange={e=>{const a=[...r.achievements];a[i]=e.target.value;update('achievements',a);}} placeholder="Ranked 45th in CodeChef Long Challenge (Global), Jun 2024" />
                    {r.achievements.length > 1 && <button onClick={()=>setResumeJson(d=>({...d,achievements:d.achievements.filter((_,j)=>j!==i)}))} className="text-gray-300 hover:text-red-400"><Trash2 size={14}/></button>}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ── LIVE PREVIEW ── */}
        <div className={`${view==='edit'?'flex-1':'w-full max-w-3xl mx-auto'} bg-white shadow-lg rounded-xl overflow-hidden`}>
          <div className="bg-gray-50 border-b border-gray-100 px-4 py-2 flex items-center justify-between">
            <span className="text-xs text-gray-400">A4 Preview</span>
            <button onClick={handlePrint} className="text-xs text-brand-600 hover:underline flex items-center gap-1"><Printer size={12}/> Print / Save as PDF</button>
          </div>
          <div ref={printRef} className="p-8" style={{ minHeight: '297mm', background: 'white' }}>
            <ModernTemplate data={resumeJson} />
          </div>
        </div>
      </div>
    </div>
  );
}
